import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLender, useBlockLender, useUnblockLender } from '../../hooks/useLenders';
import Tabs from '../../components/common/Tabs';
import LenderStatusDialog, { type LenderStatusAction } from './LenderStatusDialog';
import LenderProductsTab from './LenderProductsTab';
import LenderAccountTab from './LenderAccountTab';
import LenderUsersTab from './LenderUsersTab';
import activeStatusIcon from './assets/lender-active-status.svg';

type LenderDetailTab = 'account' | 'users' | 'products';


const lenderStatusPresentation = {
  active: {
    action: 'deactivate',
    actionVerb: 'Deactivate',
    label: 'Active',
    indicatorClassName: 'border-brand bg-brand',
    cardTitle: 'Deactivate Lender Account',
    cardDescription: 'Deactivate this lender and all associated data.',
    actionLabel: 'Deactivate Lender',
    actionClassName: 'border-red-500 text-red-500 hover:bg-red-50 focus:ring-red-500 dark:hover:bg-red-950/30',
  },
  blocked: {
    action: 'activate',
    actionVerb: 'Activate',
    label: 'Inactive',
    indicatorClassName: 'border-content-muted bg-transparent',
    cardTitle: 'Activate Lender Account',
    cardDescription: 'Activate this lender and all associated data.',
    actionLabel: 'Activate Lender',
    actionClassName: 'border-brand text-brand hover:bg-surface-sunken focus:ring-brand',
  },
} satisfies Record<'active' | 'blocked', {
  action: LenderStatusAction;
  actionVerb: string;
  label: string;
  indicatorClassName: string;
  cardTitle: string;
  cardDescription: string;
  actionLabel: string;
  actionClassName: string;
}>;

export default function LenderDetailPage() {
  const { urn } = useParams<{ urn: string }>();
  const decodedUrn = decodeURIComponent(urn!);
  const navigate = useNavigate();
  const { data: lender, isLoading, error } = useLender(decodedUrn);
  const blockLender = useBlockLender();
  const unblockLender = useUnblockLender();

  const [statusAction, setStatusAction] = useState<LenderStatusAction | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<LenderDetailTab>('account');

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !lender) {
    return (
      <div className="text-center py-20">
        <p className="text-red-500 dark:text-red-400 mb-2">Failed to load lender</p>
        <button onClick={() => navigate('/lenders')} className="btn-secondary">Back to Lenders</button>
      </div>
    );
  }

  const lenderName = lender.displayName || lender.name;
  const isStatusPending = blockLender.isPending || unblockLender.isPending;
  const statusPresentation = lenderStatusPresentation[lender.status || 'active'];

  const openStatusConfirmation = (action: LenderStatusAction) => {
    setStatusError(null);
    setStatusAction(action);
  };

  const handleStatusConfirmation = async () => {
    if (!statusAction) return;

    setStatusError(null);
    try {
      if (statusAction === 'deactivate') {
        await blockLender.mutateAsync(decodedUrn);
      } else {
        await unblockLender.mutateAsync(decodedUrn);
      }
      setStatusAction(null);
    } catch {
      setStatusError(
        statusAction === 'deactivate'
          ? 'Unable to deactivate this lender. Please try again.'
          : 'Unable to activate this lender. Please try again.',
      );
    }
  };

  return (
    <div>
      <header className="mb-8 flex flex-col gap-[7px] border-y border-[#e8ebf1] bg-surface px-[22px] py-[13px] text-content dark:border-border-subtle">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs leading-[19px]">
          <button type="button" onClick={() => navigate('/lenders')} className="font-light hover:underline">Lenders</button>
          <span aria-hidden="true" className="font-light">&gt;</span>
          <span aria-current="page" className="max-w-[294px] truncate font-medium">{lenderName}</span>
        </nav>
        <div className="flex min-h-[34px] items-center justify-between gap-4">
          <h1 className="min-w-0 truncate text-[25px] font-bold leading-10">{lenderName}</h1>
          <button
            type="button"
            onClick={() => openStatusConfirmation(statusPresentation.action)}
            aria-label={`${statusPresentation.actionVerb} ${lenderName}`}
            className="flex shrink-0 items-center gap-1 rounded px-0 py-0.5 text-[13px] font-normal leading-5 focus:outline-none focus:ring-2 focus:ring-brand"
          >
            {lender.status === 'blocked'
              ? <span aria-hidden="true" className={`h-[13px] w-[13px] rounded-full border ${statusPresentation.indicatorClassName}`} />
              : <img src={activeStatusIcon} alt="" />}
            {statusPresentation.label}
          </button>
        </div>
      </header>

      <Tabs
        variant="lender"
        tabs={[
          { key: 'account', label: 'Account' },
          { key: 'users', label: 'Users' },
          { key: 'integrations', label: 'Integrations', disabled: true },
          { key: 'products', label: 'Products' },
          { key: 'billing', label: 'Billing', disabled: true },
          { key: 'intelligent-ordering', label: 'Intelligent Ordering', disabled: true },
          { key: 'notifications', label: 'Notifications', disabled: true },
          { key: 'activity-log', label: 'Activity Log', disabled: true },
        ]}
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as LenderDetailTab)}
      >
      {activeTab === 'account' ? (
      <div className="flex flex-col gap-[30px]">
        <LenderAccountTab lender={lender} />

        <div className="card p-4 lg:p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {statusPresentation.cardTitle}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {statusPresentation.cardDescription}
          </p>
          <button
            type="button"
            onClick={() => openStatusConfirmation(statusPresentation.action)}
            className={`mt-4 inline-flex items-center justify-center rounded-lg border bg-surface px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${statusPresentation.actionClassName}`}
          >
            {statusPresentation.actionLabel}
          </button>
        </div>
      </div>
      ) : activeTab === 'users' ? (
        <LenderUsersTab lender={lender} />
      ) : (
        <LenderProductsTab lender={lender} />
      )}
      </Tabs>

      {statusAction && (
        <LenderStatusDialog
          action={statusAction}
          lenderName={lenderName}
          isPending={isStatusPending}
          errorMessage={statusError}
          onConfirm={handleStatusConfirmation}
          onCancel={() => {
            setStatusAction(null);
            setStatusError(null);
          }}
        />
      )}
    </div>
  );
}
